/**
 * Pure state-transition logic for the task-inbox bulk-select feature.
 *
 * Extracted from the `useTaskSelection` hook so the transitions can be
 * unit-tested without React rendering machinery (matches the codebase
 * convention from `persistent-view-filters-decisions.ts`).
 *
 * The hook is a thin `useState` wrapper that calls these reducers.
 */

export interface SelectionState {
  /** Currently selected task ids. */
  selected: Set<string>;
  /**
   * Index into the current page's id array of the row whose checkbox
   * was last clicked single (not range). Used as the anchor for the
   * next shift-click range select. Null until the first single-click.
   */
  anchor: number | null;
}

export const INITIAL_SELECTION_STATE: SelectionState = {
  selected: new Set(),
  anchor: null,
};

/** Toggle a single row. Updates the anchor for future shift-clicks. */
export function reduceToggle(
  state: SelectionState,
  id: string,
  indexOnPage: number
): SelectionState {
  const next = new Set(state.selected);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return { selected: next, anchor: indexOnPage };
}

/**
 * Shift-click range select. The anchor row's selection state determines
 * the operation: if the anchor is currently SELECTED, the range is
 * added (range-add); else it's removed (range-remove). Matches the
 * Gmail / Linear convention.
 *
 * Falls back to a plain toggle when there's no anchor yet (first click
 * of a session — no two endpoints to define a range).
 */
export function reduceToggleRange(
  state: SelectionState,
  id: string,
  indexOnPage: number,
  allIdsOnPage: string[]
): SelectionState {
  if (state.anchor === null) {
    return reduceToggle(state, id, indexOnPage);
  }
  const start = Math.min(state.anchor, indexOnPage);
  const end = Math.max(state.anchor, indexOnPage);
  const idsInRange = allIdsOnPage.slice(start, end + 1);
  const anchorId = allIdsOnPage[state.anchor];
  const adding = anchorId !== undefined && state.selected.has(anchorId);

  const next = new Set(state.selected);
  for (const rangeId of idsInRange) {
    if (adding) next.add(rangeId);
    else next.delete(rangeId);
  }
  return { selected: next, anchor: indexOnPage };
}

/** Select every id on the current page. Anchors at the last id. */
export function reduceSelectAll(_state: SelectionState, idsOnPage: string[]): SelectionState {
  if (idsOnPage.length === 0) return INITIAL_SELECTION_STATE;
  return {
    selected: new Set(idsOnPage),
    anchor: idsOnPage.length - 1,
  };
}

/** Reset to nothing selected, no anchor. */
export function reduceClear(): SelectionState {
  return INITIAL_SELECTION_STATE;
}
